/**
 * /reset-password/done — パスワード再設定の完了の画面（2026-09-28 creator/dashboard-pass・Core SEC-PKLOGIN-01・Kumo 管理の判断 (b)）。
 * 再設定（/reset-password）と、確認のリンクからのパスワードの決め直し（/verify-email の password_required の道）の両方が、成功の後にここへ移る。
 *
 * 出すもの（POST /v1/developers/password/reset の応答・判断と形は ../secretKeys.ts）:
 *   - removedSignInMethods: 再設定（= 持ち主の証明）で外したログイン方法（確認前に付いた物）。付け直すかは本人が決める
 *   - secretKeys: 残っているサーバーの鍵（確認の後に作られた物。自動では失効しない = 本番を止めないため）。見覚えが無ければログインしてまとめて作り直す
 * どちらも空なら、この画面は挟まずにログインへ直行する（呼ぶ側が hasResetFindings で決める）。
 *
 * WHY 同じ画面の書き換えではなく別の URL へ移るか: パスワード管理は「送信 → 画面の移動」で成功とみなし、新しいパスワードの保存を出す
 *   （2026-09-27 creator/auth-forms の WHY）。ここへ移るのでその判断は変わらない。
 * WHY 結果をメモリ（secretKeys.ts の変数）で受け取るか: URL・history.state・ストレージに残さない（secretKeys.ts の冒頭）。
 *   再読み込みで消えたらログインへ（通知メールにも同じ一覧が載っている = 見逃しても本人は後から確かめられる）。
 * 利用者が付けた名前（パスキーの名前・外部 ID のメール）はテキストとして出す（文言に混ぜず、別の要素に {} で描く）。
 */
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthShell } from './AccountRecovery';
import { SecretKeyTable } from '../components/SecretKeyTable';
import { fullDate } from '../components/ui';
import { PROVIDER_NAMES } from '../devOAuth';
import { useT } from '../i18n';
import { clearResetResult, peekResetResult, ROTATE_PATH, type RemovedSignInMethod } from '../secretKeys';

export function ResetDonePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [result] = useState(() => peekResetResult());
  // 読んだら消す（戻る・進むで古い一覧を出し直さない）。StrictMode の二重の初期化の後に消す = secretKeys.ts の peek の WHY
  useEffect(() => clearResetResult(), []);

  if (!result) return <Navigate to="/login" replace state={{ notice: 'resetDone' }} />;

  const toLogin = (from?: string) => navigate('/login', { replace: true, state: { notice: 'resetDone', ...(from ? { from } : {}) } });

  return (
    <AuthShell title={t('resetDone.title')} lead={t('resetDone.lead')}>
      {result.removedSignInMethods.length > 0 && (
        <section className="reset-done-section" aria-labelledby="rd-removed">
          <h3 id="rd-removed" className="section-title">
            {t('resetDone.removedTitle')}
          </h3>
          <p className="alert alert-warn">{t('resetDone.removedBody')}</p>
          <ul className="sk-list">
            {result.removedSignInMethods.map((m, i) => (
              <RemovedLine key={`${m.kind}-${m.createdAt}-${i}`} m={m} />
            ))}
          </ul>
        </section>
      )}
      {result.secretKeys.length > 0 && (
        <section className="reset-done-section" aria-labelledby="rd-keys">
          <h3 id="rd-keys" className="section-title">
            {t('resetDone.keysTitle')}
          </h3>
          <p className="alert alert-warn">{t('resetDone.keysBody')}</p>
          <SecretKeyTable keys={result.secretKeys} />
          <button type="button" className="btn btn-block" onClick={() => toLogin(ROTATE_PATH)}>
            {t('resetDone.rotate')}
          </button>
          <p className="small muted">{t('resetDone.rotateNote')}</p>
        </section>
      )}
      <button type="button" className="btn btn-primary btn-block" onClick={() => toLogin()}>
        {t('resetDone.login')}
      </button>
    </AuthShell>
  );
}

function RemovedLine({ m }: { m: RemovedSignInMethod }) {
  const { t } = useT();
  const name = m.kind === 'passkey' ? t('resetDone.passkey') : t('resetDone.provider', { provider: providerName(m.provider) });
  // 付け加えた名前（パスキーの label）・外部 ID のメールは本人（か入り込んだ誰か）が決めた値 = 文言に差し込まず、別の要素でテキストとして出す
  const detail = m.kind === 'passkey' ? m.label : m.email;
  return (
    <li className="sk-list-item">
      <div>
        <strong>{name}</strong>
        {detail && <span className="sk-text"> · {detail}</span>}
      </div>
      <div className="small muted">
        {t('resetDone.added', { date: fullDate(m.createdAt) })} · {m.lastUsedAt ? t('resetDone.lastUsed', { date: fullDate(m.lastUsedAt) }) : t('resetDone.neverUsed')}
      </div>
    </li>
  );
}

/** 知っているプロバイダは表示名、知らない値（将来の追加）はそのまま（テキストとして） */
function providerName(p: string) {
  return p in PROVIDER_NAMES ? PROVIDER_NAMES[p as keyof typeof PROVIDER_NAMES] : p;
}
